import { differenceInCalendarDays, differenceInDays } from 'date-fns';
import PropertyTableRowModel from '../components/Underwriting/components/PropertyTable/PropertyTableRowModel';
import BRPropertyModel from '../views/SubmissionDetails/components/UnderwriterReview/components/BuildersRisk/Property/BRPropertyModel';
import { isNullOrUndefined } from './object';

//region Rate Calculations
export const calculateFinalRate = (creditDebit: number = 0, baseRate?: number) => {
    if (!baseRate) return 0;
    // @ts-ignore
    const cdDb = creditDebit === '' ? 0 : creditDebit || 0;
    // Note: removing the * 1000 / 1000 up the rounding
    // rates go to the tenth of a penny
    return Math.round((baseRate * (1 + cdDb / 100) + 0.00001) * 1000) / 1000;
};
//endregion

//region Premium Calculations

// Note - this output includes TRIA.
export const calculateTotalPremiumFromRateAndLimit = (
    finalRate: number = 0,
    totalInsurableValue: number = 0,
    triaPercentage = 0,
    policyStartDate: string | undefined,
    policyEndDate: string | undefined
) => {
    if (!policyEndDate || !policyStartDate) {
        console.error(
            'Policy start and end dates are required to calculate total premium from rate and limit. Setting premium to $0.'
        );
        return 0;
    }
    return addTriaPremiumToRatedPremium(
        calculatePremiumFromRateAndLimit(finalRate, totalInsurableValue, policyStartDate, policyEndDate),
        triaPercentage
    );
};

// Note - this output does not include TRIA.
export const calculatePremiumFromRateAndLimit = (
    rate: number | undefined,
    limit: number | undefined,
    policyStartDate: string | undefined,
    policyEndDate: string | undefined
) => {
    if (!rate || !limit || !policyStartDate || !policyEndDate) return 0;

    return (
        calculateAnnualizedPremiumFromRateAndLimit(rate, limit) *
        (differenceInDays(new Date(policyEndDate), new Date(policyStartDate)) / 365)
    );
};

export const calculateTotalPolicyPremiumFromLocations = (locations: PropertyTableRowModel[] | BRPropertyModel[]) => {
    return locations.length ? locations.reduce((acc, curVal) => acc + (curVal.totalPremium || 0), 0) : 0;
};
//endregion

//region Annualized Premium Calculations
export const calculateAnnualizedPremiumFromRateAndLimit = (rate: number = 0, limit: number = 0) => {
    return (rate * limit) / 100;
};

export const calculateAnnualizedPremiumFromTotalPremium = (
    totalPremium: number,
    policyStartDate?: string,
    policyEndDate?: string
): number => {
    return Math.ceil(
        totalPremium / (differenceInDays(new Date(policyEndDate || ''), new Date(policyStartDate || '')) / 365)
    );
};

export const calculateTotalPremiumFromAnnualizedPremium = (
    annualizedPremium: number,
    policyStartDate?: string,
    policyEndDate?: string
): number => {
    return Math.ceil(
        annualizedPremium * (differenceInDays(new Date(policyEndDate || ''), new Date(policyStartDate || '')) / 365)
    );
};
//endregion

//region TRIA Calculations
export const addTriaPremiumToRatedPremium = (ratedPremium: number = 0, triaPercentage = 0) => {
    const rawTotal = Math.round(ratedPremium * (1 + triaPercentage / 100) * 100) / 100;
    return Math.ceil(rawTotal); // round up to the nearest dollar
};

export const calculateTriaPremiumFromTotalPremium = (totalPremium: number, triaPercentage: number): number => {
    const tp = triaPercentage / 100;
    return totalPremium / (1 + 1 / tp);
};

export const calculateTriaPremiumFromRatedPremium = (ratedPremium: number, triaPercentage: number): number => {
    return ratedPremium * (triaPercentage / 100);
};
//endregion

//region Misc Additional Calculations
export const calculateEndorsementPremium = (
    totalPremium: number,
    submissionEffectiveDate: string,
    submissionExpDate: string,
    endorsementEffectiveDate: string,
    endorsementExpDate?: string
) => {
    if (!endorsementExpDate) {
        endorsementExpDate = submissionExpDate;
    }
    // Note: [TOTAL PREMIUM] / ([EXP DATE - EFF DATE]/[DAYS IN YEAR])
    const dayDiff = differenceInCalendarDays(new Date(endorsementExpDate), new Date(endorsementEffectiveDate));
    const daysInYear = differenceInCalendarDays(new Date(submissionExpDate), new Date(submissionEffectiveDate));
    const rawTotal = totalPremium * (dayDiff / daysInYear);
    return Math.ceil(rawTotal);
};

export const calculateAdditionalBrokerPremium = (
    isBrokerCommissionRated: boolean,
    premium: number,
    brokerCommissionPercentage: number
) =>
    isBrokerCommissionRated || brokerCommissionPercentage === 0
        ? premium
        : Math.ceil(premium / (1 - brokerCommissionPercentage)); // we are always rounding up
//endregion
